import { useEffect, useMemo, useState, useRef } from "react";
import SearchInput from "./shared/SearchInput";
import HelpButton from "./shared/HelpButton";
import { fetchSouthyarra, fetchTkEmailCandidates, fetchTkCcDefault, saveTkCcDefault,
  syReportPreview, syReportSend } from "../utils/api";

/* ============================================================================
 * IgluDashboard — the South Yarra cleaning table, by month.
 *   · month navigation (monthYear "Aug2026" → FilterMonthYear on the backend)
 *   · search by room, cleaner or room type
 *   · ⚡ Same-Day chip + 📅 specific Clean Dates (multi-select)
 *   · eight clickable pipeline totals (2 rows of 4) that filter the table:
 *       Row 1 (cleaning):  Unclaimed | Claimed in Advance | Overdue | Done – No Date
 *       Row 2 (approval):  Completed | Approved – Not Invoiced | Ready to Invoice | Please Delete
 *   · rows grouped by Clean Date with a day header, TimeKeeping-style
 *   · ✉ report modal — preview / email PDF of the current filter
 * ==========================================================================*/

const MONTHS3 = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/* mirror of field_keys in helper.py get_filter_data_sy — order matters */
const FIELD_KEYS = [
  "Request Sent", "Room Number", "Clean Date", "Mattress Protector",
  "Room Type", "Bedroom", "Bathroom", "Kitchen or Common Area",
  "Comments", "Cleaning Status", "Email", "Same Day Turnover",
  "South Yarra Approval", "Today Clean Day", "Cleaner Attend",
  "Cleaning Allocated Minutes", "Allocated", "Cleaners Email", "Cleaners Rate",
  "South Yarra Costs", "Completed Work Date", "Extra Time", "Extrawork Payment",
  "SNL Extrawork Payment", "Extra Work Approval", "Clean Status",
  "SouthYarraApproved", "RoomTypes", "Completed Clean Date",
];
const toObjects = (arrs) => (arrs || []).map((a) => {
  const o = { id: a[0] };
  FIELD_KEYS.forEach((k, i) => { o[k] = a[i + 1]; });
  return o;
});

/* ---------- lifecycle predicates ---------- */
const DEF_NAME = "Cleaner Name";
const DEF_EMAIL = "enteryouremail@yahoo.com.au";
const isDefault = (r) =>
  (r["Cleaner Attend"] || "") === DEF_NAME && (r["Cleaners Email"] || "") === DEF_EMAIL;
const isClaimed = (r) =>
  r["Cleaner Attend"] && r["Cleaner Attend"] !== DEF_NAME &&
  r["Cleaners Email"] && r["Cleaners Email"] !== DEF_EMAIL;
const hasDoneDate = (r) => !!r["Completed Clean Date"];
const isSameDay = (r) => r["Same Day Turnover"] === "Yes";
const todayISO = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
};
const pastDue = (r) => r["Clean Date"] && r["Clean Date"] < todayISO();     // ISO compare
const futureClean = (r) => r["Clean Date"] && r["Clean Date"] > todayISO();
const notDone = (r) => r["Cleaning Status"] !== "Cleaning Completed" && !hasDoneDate(r);

/* eight pipeline totals — 2 rows of 4, in lifecycle order.
 * "Claimed in Advance" replaces the same-day-overdue subset (that count still
 * shows as a sub-label on Overdue, and ⚡ marks same-day rows in the table).
 * To restore the old button:  test:(r)=>isSameDay(r)&&pastDue(r)&&isDefault(r)&&notDone(r) */
const FILTERS = [
  /* row 1 — cleaning pipeline */
  { key:"unclaimed", label:"Unclaimed",             tone:"grey",
    test:(r) => isDefault(r) && !hasDoneDate(r) },
  { key:"advance",   label:"Claimed in Advance",    tone:"amber",
    test:(r) => isClaimed(r) && futureClean(r) },
  { key:"overdue",   label:"Overdue",               tone:"red",
    test:(r) => pastDue(r) && isDefault(r) && notDone(r),
    sub:(r) => isSameDay(r) },                                   // ⚡ count inside
  { key:"nodate",    label:"Done - No Date",        tone:"amber",
    test:(r) => isClaimed(r) && r["Cleaning Status"] === "Cleaning Completed" && !hasDoneDate(r) },
  /* row 2 — approval pipeline */
  { key:"done",      label:"Completed",             tone:"green",
    test:(r) => isClaimed(r) && r["Cleaning Status"] === "Cleaning Completed" && hasDoneDate(r) },
  { key:"apprwait",  label:"Approved · Not Invoiced", tone:"amber",
    test:(r) => r["South Yarra Approval"] === "Approved" && r["Cleaning Status"] !== "Ready for invoicing" },
  { key:"invoice",   label:"Ready to Invoice",      tone:"green",
    test:(r) => r["South Yarra Approval"] === "Approved" && r["Cleaning Status"] === "Ready for invoicing" },
  { key:"delete",    label:"Please Delete",         tone:"red",
    test:(r) => r["Cleaning Status"] === "Please Delete" },
];

const toneColor = (t) =>
  t === "red" ? "#f87171" : t === "green" ? "#4ade80" : t === "amber" ? "#fbbf24" : "#94a3b8";

const S = {
  backdrop: { position:"fixed", inset:0, top: "var(--fr-modal-top, 0px)", background:"rgba(2,6,23,0.7)", zIndex:1100,
    display:"flex", alignItems:"flex-start", justifyContent:"center", padding:"40px 16px", overflowY:"auto" },
  modal: { position:"relative", maxHeight: "calc(var(--fr-modal-max, 82vh) - 76px)", overflowY: "auto", overscrollBehavior: "contain", width:"100%", maxWidth:1200,
    background:"linear-gradient(135deg,#1e293b 0%,#0f172a 100%)",
    border:"1px solid rgba(34,211,238,0.25)", borderRadius:16, padding:"18px 18px 14px" },
  accent: { position:"absolute", top:0, left:0, right:0, height:3,
    background:"linear-gradient(90deg,#22d3ee,#3b82f6,#8b5cf6)" },
  head: { position: "sticky", top: 0, zIndex: 5, background: "#1e293b", boxShadow: "0 -18px 0 0 #1e293b",  display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 },
  title: { margin:0, fontSize:"1rem", fontWeight:700, color:"#f1f5f9" },
  closeX: { background:"none", border:"none", color:"rgba(255,255,255,0.6)", fontSize:"1.4rem", lineHeight:1, cursor:"pointer" },
  nav: { display:"flex", alignItems:"center", justifyContent:"center", gap:14, margin:"2px 0 10px" },
  navBtn: { width:32, height:32, borderRadius:9, border:"1px solid rgba(34,211,238,0.4)",
    background:"rgba(34,211,238,0.1)", color:"#22d3ee", cursor:"pointer", fontSize:"0.95rem", lineHeight:1 },
  navLabel: { minWidth:120, textAlign:"center", fontWeight:800, color:"#f1f5f9", fontSize:"0.95rem", letterSpacing:"0.02em" },
  chipsRow: { display:"flex", flexWrap:"wrap", gap:6, margin:"8px 0" },
  dayChip: (on) => ({ padding:"4px 12px", borderRadius:999, fontSize:"0.72rem", fontWeight:700, cursor:"pointer",
    border:`1px solid ${on ? "rgba(59,130,246,0.7)" : "rgba(255,255,255,0.15)"}`,
    background: on ? "rgba(59,130,246,0.2)" : "rgba(255,255,255,0.03)",
    color: on ? "#93c5fd" : "rgba(255,255,255,0.55)" }),
  buttonsGrid: { display:"grid", gridTemplateColumns:"repeat(4,minmax(0,1fr))", gap:10, margin:"10px 0 6px" },
  countBtn: (active) => ({ borderRadius:12, padding:"11px 8px", cursor:"pointer",
    background: active ? "rgba(34,211,238,0.15)" : "rgba(34,211,238,0.08)",
    border:`1px solid ${active ? "rgba(34,211,238,0.6)" : "rgba(34,211,238,0.25)"}`,
    display:"flex", flexDirection:"column", alignItems:"center", gap:4 }),
  countLabel: { fontSize:"0.62rem", color:"rgba(255,255,255,0.55)", textTransform:"uppercase",
    letterSpacing:"0.05em", fontWeight:700, textAlign:"center", lineHeight:1.3 },
  countValue: (tone) => ({ fontSize:"1.55rem", fontWeight:800, lineHeight:1, color: toneColor(tone) }),
  countSub: { fontSize:"0.6rem", color:"rgba(255,255,255,0.45)", fontWeight:700 },
  card: { background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)",
    borderRadius:12, padding:"12px", marginTop:8 },
  dateHead: { display:"flex", justifyContent:"space-between", alignItems:"baseline",
    padding:"10px 8px 6px", position:"sticky", top:0, background:"#1b2537", zIndex:1 },
  dateTitle: { fontSize:"0.78rem", fontWeight:800, color:"#22d3ee", letterSpacing:"0.04em" },
  dateMeta: { fontSize:"0.7rem", color:"rgba(255,255,255,0.45)" },
  colHead: { fontSize:"0.62rem", letterSpacing:"0.05em", textTransform:"uppercase",
    color:"rgba(255,255,255,0.45)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  cell: { fontSize:"0.82rem", color:"#e2e8f0", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  name: { fontSize:"0.84rem", fontWeight:700, color:"#22d3ee", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  dim: { fontSize:"0.82rem", color:"rgba(255,255,255,0.35)", fontStyle:"italic",
    whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  badge: (kind) => ({ fontSize:"0.6rem", fontWeight:700, letterSpacing:"0.03em",
    padding:"1px 7px", borderRadius:999, whiteSpace:"nowrap",
    background: kind === "ok" ? "rgba(74,222,128,0.12)" : kind === "warn" ? "rgba(251,191,36,0.14)" :
      kind === "bad" ? "rgba(248,113,113,0.12)" : "rgba(148,163,184,0.12)",
    color: kind === "ok" ? "#4ade80" : kind === "warn" ? "#fbbf24" : kind === "bad" ? "#f87171" : "#94a3b8",
    border: `1px solid ${kind === "ok" ? "rgba(74,222,128,0.35)" : kind === "warn" ? "rgba(251,191,36,0.4)" :
      kind === "bad" ? "rgba(248,113,113,0.4)" : "rgba(148,163,184,0.35)"}` }),
  empty: { padding:"24px", textAlign:"center", color:"rgba(255,255,255,0.5)", fontStyle:"italic", fontSize:"0.85rem" },
  closeBtn: { width:"100%", padding:"11px", borderRadius:10, border:"none",
    background:"rgba(255,255,255,0.06)", color:"#e2e8f0", fontWeight:600, cursor:"pointer", marginTop:12 },
};

/* Room | Type | Status | Approval | Done Date | Cleaner */
const TABLE_COLS = "0.8fr 1.3fr 1.1fr 1fr 0.9fr 1.1fr";

/* ---------- nested overlay styles (recipients + send) ---------- */
const O = {
  overlay: { position:"fixed", inset:0, top: "var(--fr-modal-top, 0px)", background:"rgba(0,0,0,0.75)", backdropFilter:"blur(6px)",
    display:"flex", alignItems:"center", justifyContent:"center", zIndex:1200, padding:20 },
  modal: { background:"linear-gradient(135deg,#0f172a 0%,#1e293b 100%)", border:"1px solid rgba(34,211,238,0.25)",
    borderRadius:16, width:"100%", maxWidth:640, overflow:"hidden", boxShadow:"0 25px 80px rgba(0,0,0,0.6)" },
  bar: { height:3, background:"linear-gradient(90deg,#22d3ee,#3b82f6,#8b5cf6)" },
  header: { padding:"16px 22px 8px", display:"flex", justifyContent:"space-between", alignItems:"center" },
  hTitle: { fontSize:"1rem", fontWeight:700, color:"#f1f5f9", margin:0 },
  body: { padding:"8px 22px 4px", maxHeight:"min(60vh, calc(100dvh - var(--fr-modal-top, 0px) - 200px))", overflowY:"auto" },
  footer: { padding:"14px 22px", display:"flex", justifyContent:"flex-end", gap:10,
    background:"rgba(0,0,0,0.2)", borderTop:"1px solid rgba(255,255,255,0.06)" },
  cancel: { background:"rgba(255,255,255,0.06)", color:"rgba(255,255,255,0.7)", border:"1px solid rgba(255,255,255,0.1)",
    borderRadius:10, padding:"10px 18px", fontSize:"0.85rem", fontWeight:600, cursor:"pointer" },
  confirm: (busy) => ({ background:"linear-gradient(135deg,#22d3ee 0%,#3b82f6 100%)", color:"#0f172a", border:"none",
    borderRadius:10, padding:"10px 20px", fontSize:"0.85rem", fontWeight:700,
    cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1,
    display:"inline-flex", alignItems:"center", gap:8 }),
  row: { display:"grid", gridTemplateColumns:"28px 1.2fr 1fr 1.4fr", alignItems:"center",
    columnGap:8, padding:"9px 4px", borderBottom:"1px solid rgba(255,255,255,0.06)" },
  rName: { fontSize:"0.85rem", fontWeight:700, color:"#22d3ee", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" },
  rCell: { fontSize:"0.8rem", color:"#e2e8f0", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" },
  pill: { fontSize:"0.72rem", color:"rgba(255,255,255,0.55)" },
  check: { width:16, height:16, accentColor:"#22d3ee", cursor:"pointer" },
  note: { padding:"8px 12px", borderRadius:8, margin:"8px 0", fontSize:"0.78rem",
    background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.35)", color:"#4ade80" },
  err: { padding:"8px 12px", borderRadius:8, margin:"8px 0", fontSize:"0.78rem",
    background:"rgba(248,113,113,0.08)", border:"1px solid rgba(248,113,113,0.35)", color:"#fca5a5" },
};

const Spin = () => (
  <span style={{ width:12, height:12, border:"2px solid currentColor", borderTopColor:"transparent",
    borderRadius:"50%", display:"inline-block", animation:"ig-spin 0.7s linear infinite" }} />
);

/* ---------- send report — same recipient pool + CC-default chips as TimeKeeping ---------- */
const SendModal = ({ filterHeader, reportRows, summary, onClose }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState("");             // "preview" | "send"
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  const CC_CATEGORIES = [
    { key: "supervisor", label: "Supervisors" },
    { key: "admin",      label: "Admin" },
    { key: "operations", label: "Operations" },
  ];
  const catOf = (r) => r.category || "operations";
  const [chips, setChips] = useState([]);

  useEffect(() => {
    Promise.all([fetchTkEmailCandidates(), fetchTkCcDefault()]).then(([cand, def]) => {
      const candidates = cand.status === "success" ? cand.candidates || [] : [];
      const active = def.status === "success" && Array.isArray(def.chips) ? def.chips : ["supervisor"];
      setList(candidates);
      setChips(active);
      const pre = {};
      candidates.forEach((r) => { if (active.includes(catOf(r))) pre[r.email] = true; });
      setChecked(pre);
      setLoading(false);
    });
  }, []);

  const toggleChip = (key) => {
    const next = chips.includes(key) ? chips.filter((c) => c !== key) : [...chips, key];
    setChips(next);
    const pre = {};
    list.forEach((r) => { if (next.includes(catOf(r))) pre[r.email] = true; });
    setChecked(pre);
    saveTkCcDefault(next);
  };
  const allChecked = list.length > 0 && list.every((r) => checked[r.email]);
  const toggleAll = () => {
    const next = {};
    if (!allChecked) list.forEach((r) => { next[r.email] = true; });
    setChecked(next);
  };
  const recipients = list.filter((r) => checked[r.email])
    .map((r) => ({ name: r.name, email: r.email }));

  const preview = async () => {
    setBusy("preview"); setError(""); setNote("");
    const res = await syReportPreview(filterHeader, reportRows, summary);
    if (res.status === "success") {
      window.open(URL.createObjectURL(res.blob), "_blank");
    } else setError(res.error || "Preview failed");
    setBusy("");
  };

  const send = async () => {
    setBusy("send"); setError(""); setNote("");
    const res = await syReportSend(filterHeader, reportRows, recipients, summary);
    if (res.status === "success") {
      setNote(`Sent — ${recipients.length} personalised email${recipients.length === 1 ? "" : "s"} + work inbox copy.`);
    } else setError(res.error || "Send failed");
    setBusy("");
  };

  return (
    <div style={O.overlay} onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }}>
      <div style={O.modal}>
        <div style={O.bar} />
        <div style={O.header}>
          <h5 style={O.hTitle}>✉ Send Iglu Report</h5>
          <span style={O.pill}>{reportRows.length} rooms in filter</span>
        </div>
        <div style={O.body}>
          <div style={{ ...O.pill, margin:"2px 0 8px" }}>{filterHeader}</div>
          <div style={{ display:"flex", gap:6, margin:"4px 0 10px" }}>
            {CC_CATEGORIES.map((c) => (
              <button key={c.key} type="button" style={S.dayChip(chips.includes(c.key))}
                onClick={() => toggleChip(c.key)}>
                {c.label}
              </button>
            ))}
          </div>
          {note && <div style={O.note}>{note}</div>}
          {error && <div style={O.err}>{error}</div>}
          <div style={{ ...O.row, borderBottom:"1px solid rgba(255,255,255,0.12)" }}>
            <input type="checkbox" style={O.check} checked={allChecked} onChange={toggleAll}
              title="Select all" />
            {["Name", "Job", "Email"].map((h) => (
              <span key={h} style={{ fontSize:"0.62rem", textTransform:"uppercase",
                letterSpacing:"0.05em", color:"rgba(255,255,255,0.45)", fontWeight:700 }}>{h}</span>
            ))}
          </div>
          {loading ? (
            <div style={{ padding:20, textAlign:"center", color:"rgba(255,255,255,0.5)" }}>Loading…</div>
          ) : list.map((r) => (
            <div key={r.email} style={O.row}>
              <input type="checkbox" style={O.check} checked={!!checked[r.email]}
                onChange={() => setChecked((p) => ({ ...p, [r.email]: !p[r.email] }))} />
              <span style={O.rName}>{r.name}</span>
              <span style={O.rCell}>{r.job || "—"}</span>
              <span style={O.rCell}>{r.email}</span>
            </div>
          ))}
        </div>
        <div style={O.footer}>
          <button type="button" style={O.cancel} onClick={onClose}>Close</button>
          <button type="button" style={O.confirm(busy === "preview")} onClick={preview}
            disabled={!!busy || reportRows.length === 0}>
            {busy === "preview" ? <Spin /> : "👁"} Preview Report
          </button>
          <button type="button" style={O.confirm(busy === "send")} onClick={send}
            disabled={!!busy || reportRows.length === 0}>
            {busy === "send" ? <Spin /> : "✉"} Send Emails ({recipients.length})
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------- badge helpers ---------- */
const statusKind = (s) =>
  s === "Ready for invoicing" ? "ok" :
  s === "Cleaning Completed" ? "ok" :
  s === "Please Delete" ? "bad" : "warn";
const approvalKind = (a) => a === "Approved" ? "ok" : a ? "warn" : "grey";

const IgluDashboard = ({ onClose, refreshKey = 0, onBusy }) => {
  const inline = !onClose;                    // rendered inside a tab, not as a modal
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth());          // 0-11
  const [year, setYear] = useState(now.getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);         // ↻ soft refresh
  const [query, setQuery] = useState("");
  const [sameDayOnly, setSameDayOnly] = useState(false);       // ⚡ chip
  const [filters, setFilters] = useState([]);                  // pipeline buttons
  const [sendOpen, setSendOpen] = useState(false);
  const [dates, setDates] = useState([]);                      // selected ISO Clean Dates
  const dateRef = useRef(null);
  const monthYear = `${MONTHS3[month]}${year}`;                // "Aug2026"
  const mm = String(month + 1).padStart(2, "0");
  const lastDay = new Date(year, month + 1, 0).getDate();
  const dateMin = `${year}-${mm}-01`;
  const dateMax = `${year}-${mm}-${String(lastDay).padStart(2, "0")}`;

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetchSouthyarra({ monthYear });
        if (alive) setRows(res.status === "success" ? toObjects(res.southyarra) : []);
      } catch {
        if (alive) setRows([]);
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [monthYear]);

  /* soft refresh — refetch the same month in place: keeps the table visible
     and every filter, chip, date and search exactly as they are */
  const refresh = async () => {
    if (refreshing || loading) return;
    setRefreshing(true);
    try {
      const res = await fetchSouthyarra({ monthYear });
      if (res.status === "success") setRows(toObjects(res.southyarra));
    } catch { /* keep current rows on a failed refresh */ }
    setRefreshing(false);
  };


useEffect(() => { if (refreshKey) refresh(); }, [refreshKey]);   // eslint-disable-line react-hooks/exhaustive-deps

useEffect(() => { onBusy?.(loading || refreshing); }, [loading, refreshing, onBusy]);


  useEffect(() => {
    if (inline) return;                       // no Escape-to-close inside a tab
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, inline]);

  const nav = (d) => {
    let m = month + d, y = year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setMonth(m); setYear(y); setFilters([]); setDates([]);
  };
  const fmtDate = (iso) => {
    if (!iso || !iso.includes("-")) return iso || "—";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  };

  /* ⚡ jump: today's exceptions — overdue + claimed-in-advance across the month */
  const exceptionsPreset = () => {
    setFilters(["overdue", "advance"]);
    setDates([]); setSameDayOnly(false); setQuery("");
  };

  /* chips+search scope (pipeline counts compute on this) */
  const scoped = useMemo(() => {
    const q = query.toLowerCase().replace(/\s+/g, "");
    return rows.filter((r) => {
      if (dates.length && !dates.includes(r["Clean Date"])) return false;
      if (sameDayOnly && !isSameDay(r)) return false;
      if (q) {
        const hay = `${r["Room Number"]}${r["Cleaner Attend"]}${r["Room Type"]}${r["Cleaning Status"]}`
          .toLowerCase().replace(/\s+/g, "");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, dates, sameDayOnly, query]);

  const counts = useMemo(() => {
    const c = {}, sub = {};
    FILTERS.forEach((f) => {
      const hit = scoped.filter(f.test);
      c[f.key] = hit.length;
      if (f.sub) sub[f.key] = hit.filter(f.sub).length;
    });
    return { c, sub };
  }, [scoped]);

  const visible = useMemo(() => {
    if (!filters.length) return scoped;
    const active = FILTERS.filter((f) => filters.includes(f.key));
    return scoped.filter((r) => active.some((f) => f.test(r)));
  }, [scoped, filters]);

  /* group by Clean Date, ascending */
  const groups = useMemo(() => {
    const g = {};
    visible.forEach((r) => { (g[r["Clean Date"]] = g[r["Clean Date"]] || []).push(r); });
    return Object.keys(g).sort().map((d) => ({
      date: d,
      sameDay: g[d].filter(isSameDay).length,
      rows: g[d].sort((a, b) => String(a["Room Number"] || "").localeCompare(String(b["Room Number"] || ""), undefined, { numeric:true })),
    }));
  }, [visible]);

  const summary = useMemo(() => ({
    totalRooms: scoped.length,
    sameDay: scoped.filter(isSameDay).length,
    counts: counts.c,                          // all eight pipeline totals from scoped
  }), [scoped, counts]);

  const filterHeader = useMemo(() => {
    const parts = [`${MONTHS3[month]} ${year}`];
    if (sameDayOnly) parts.push("Same Day Turnover only");
    if (dates.length) parts.push(`Clean Dates: ${dates.map(fmtDate).join(", ")}`);
    if (query.trim()) parts.push(`Search: "${query.trim()}"`);
    const act = FILTERS.filter((x) => filters.includes(x.key)).map((x) => x.label);
    parts.push(act.length ? `Filter: ${act.join(" + ")}` : "Filter: All rooms");
    return parts.join(" · ");
  }, [month, year, sameDayOnly, dates, query, filters]);

  /* rows serialised for the PDF backend — named fields only, plus which of
     the ACTIVE filter buttons each row matched (per-group attribution) */
  const reportRows = useMemo(() => {
    const active = FILTERS.filter((f) => filters.includes(f.key));
    return visible.map((r) => ({
      "Request Sent": r["Request Sent"], "Room Number": r["Room Number"],
      "Clean Date": r["Clean Date"], "Room Type": r["Room Type"],
      "Cleaning Status": r["Cleaning Status"], "South Yarra Approval": r["South Yarra Approval"],
      "Cleaner Attend": isClaimed(r) ? r["Cleaner Attend"] : "Unclaimed",
      "Same Day Turnover": r["Same Day Turnover"], "Completed Clean Date": r["Completed Clean Date"],
      "Matched": active.filter((f) => f.test(r)).map((f) => f.label),
    }));
  }, [visible, filters]);

  const shell = (
    <>
      <style>{`@keyframes ig-spin{to{transform:rotate(360deg)}}
        .ig-row{transition:background .12s ease}.ig-row:hover{background:rgba(34,211,238,0.06)}`}</style>
      <div style={{ ...S.modal, margin: inline ? "0 auto" : undefined }}
        onClick={(e) => e.stopPropagation()}>
        <div style={S.accent} />
        <div style={S.head}>
            <h4 style={S.title}>Iglu Dashboard — South Yarra</h4>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <HelpButton helpData={[
                { chip: 'southyarradashboard', urlFilter: '/static/pdffilters/SouthYarraDashboard.pdf' }
              ]} />
              <button type="button" style={{ ...S.navBtn, opacity: refreshing ? 0.6 : 1 }}
                title="Refresh data — keeps current filters" onClick={refresh}>
                {refreshing ? <Spin /> : "↻"}
              </button>
              {/* ⚡ and ✉ buttons unchanged */}
            <button type="button" style={S.navBtn} title="Exceptions — overdue & claimed in advance"
              onClick={exceptionsPreset}>⚡</button>
            <button type="button" style={S.navBtn} title="Send report for the current filter"
              onClick={() => setSendOpen(true)}>✉</button>
            {!inline && (
              <button type="button" style={S.closeX} onClick={onClose} aria-label="Close">×</button>
            )}
          </div>
        </div>

        {/* month navigation */}
        <div style={S.nav}>
          <button type="button" style={S.navBtn} onClick={() => nav(-1)}>◄</button>
          <span style={S.navLabel}>{MONTHS3[month]} {year}</span>
          <button type="button" style={S.navBtn} onClick={() => nav(1)}>►</button>
        </div>

        <SearchInput value={query} onChange={setQuery} placeholder="Search room, cleaner or type…" />

        {/* ⚡ same-day chip + specific Clean Dates */}
        <div style={S.chipsRow}>
          <button type="button" style={S.dayChip(sameDayOnly)} title="Same Day Turnover rooms only"
            onClick={() => setSameDayOnly((p) => !p)}>
            ⚡ Same Day
          </button>
          <button type="button" style={S.dayChip(dates.length > 0)} title="Filter specific Clean Dates"
            onClick={() => dateRef.current?.showPicker?.() || dateRef.current?.click()}>
            📅
          </button>
          <input ref={dateRef} type="date" min={dateMin} max={dateMax}
            style={{ position:"absolute", opacity:0, pointerEvents:"none", width:0, height:0 }}
            onChange={(e) => {
              const v = e.target.value;
              if (v) setDates((p) => (p.includes(v) ? p : [...p, v]));
              e.target.value = "";
            }} />
          {dates.map((d) => (
            <button key={d} type="button" style={S.dayChip(true)} title="Remove"
              onClick={() => setDates((p) => p.filter((x) => x !== d))}>
              {fmtDate(d)} ×
            </button>
          ))}
        </div>

        {/* pipeline totals — 2 rows of 4 clickable filters, lifecycle order */}
        {[FILTERS.slice(0, 4), FILTERS.slice(4)].map((row, i) => (
          <div key={i} style={{ ...S.buttonsGrid, margin: i ? "0 0 6px" : "10px 0 10px" }}>
            {row.map((f) => (
              <button key={f.key} type="button" style={S.countBtn(filters.includes(f.key))}
                onClick={() => setFilters((p) =>
                  p.includes(f.key) ? p.filter((x) => x !== f.key) : [...p, f.key])}>
                <span style={S.countLabel}>{f.label}</span>
                <span style={S.countValue(f.tone)}>{loading ? "…" : counts.c[f.key]}</span>
                {f.sub && !loading && counts.sub[f.key] > 0 && (
                  <span style={S.countSub}>⚡ {counts.sub[f.key]} same day</span>
                )}
              </button>
            ))}
          </div>
        ))}

        {/* grouped table */}
        <div style={S.card}>
          {loading ? (
            <div style={S.empty}>Loading {MONTHS3[month]} {year}…</div>
          ) : groups.length === 0 ? (
            <div style={S.empty}>No rooms for this view.</div>
          ) : (
            <div style={{ maxHeight:420, overflowY:"auto", overflowX:"auto" }}>
              <div style={{ minWidth:720 }}>
                {groups.map((g) => (
                  <div key={g.date}>
                    <div style={S.dateHead}>
                      <span style={S.dateTitle}>
                        Clean Date · {fmtDate(g.date)}{g.sameDay > 0 ? ` · ⚡ ${g.sameDay} same day` : ""}
                      </span>
                      <span style={S.dateMeta}>
                        {g.rows.length} room{g.rows.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:TABLE_COLS, padding:"4px 8px 8px",
                      columnGap:8, borderBottom:"1px solid rgba(255,255,255,0.12)" }}>
                      {["Room","Type","Status","Approval","Done Date","Cleaner"].map((h) => (
                        <span key={h} style={S.colHead}>{h}</span>
                      ))}
                    </div>
                    {g.rows.map((r, i) => (
                      <div key={r.id || `${g.date}-${i}`} className="ig-row"
                        style={{ display:"grid", gridTemplateColumns:TABLE_COLS, alignItems:"center",
                          padding:"9px 8px", columnGap:8,
                          borderTop: i ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                        <span style={S.name}>
                          {isSameDay(r) ? "⚡ " : ""}{r["Room Number"] || "—"}
                        </span>
                        <span style={S.cell}>{r["Room Type"] || "—"}</span>
                        <span>
                          <span style={S.badge(statusKind(r["Cleaning Status"]))}>
                            {r["Cleaning Status"] || "Pending"}
                          </span>
                        </span>
                        <span>
                          <span style={S.badge(approvalKind(r["South Yarra Approval"]))}>
                            {r["South Yarra Approval"] || "—"}
                          </span>
                        </span>
                        <span style={{ ...S.cell,
                          color: hasDoneDate(r) ? "#4ade80" :
                            (r["Cleaning Status"] === "Cleaning Completed" ? "#f87171" : "#e2e8f0") }}>
                          {hasDoneDate(r) ? fmtDate(r["Completed Clean Date"]) : "—"}
                        </span>
                        {isClaimed(r)
                          ? <span style={S.name}>{r["Cleaner Attend"]}</span>
                          : <span style={S.dim}>Unclaimed</span>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {!inline && (
          <button type="button" style={S.closeBtn} onClick={onClose}>Close</button>
        )}
        {sendOpen && (
          <SendModal filterHeader={filterHeader} reportRows={reportRows} summary={summary}
            onClose={() => setSendOpen(false)} />
        )}
      </div>
    </>
  );
  return inline ? shell : (
    <div style={S.backdrop} onClick={onClose}>{shell}</div>
  );
};

export default IgluDashboard;